import React from "react";
import styled from "styled-components";
import { AdminStationPortalLogRecord } from "../../../services/StationPortalLogService";
import { getActorLabel, getStationLabel } from "./LogTable";

/**
 * metadata phần lớn sự kiện (page_view, user_interaction...) không kèm payload
 * nên thường là null. JSON.stringify(null, ...) trả về chuỗi "null" chứ không
 * ném lỗi, nhưng hiện dấu gạch ngang rõ nghĩa hơn cho người đọc log so với chữ
 * "null". Bọc try/catch phòng metadata có tham chiếu vòng (circular) — dù
 * payload từ API JSON hoá rồi thì khó xảy ra, nhưng cái giá phòng vẫn rẻ.
 */
function formatMetadata(metadata: Record<string, any> | null | undefined): string {
	if (!metadata) return "-";
	try {
		return JSON.stringify(metadata, null, 2);
	} catch (_err) {
		return "-";
	}
}

// Cùng bảng màu với LogTable.tsx / TimeRangePicker.tsx / phần style nội tuyến
// của StationPortalLogs.tsx.
const Wrap = styled.div`
	padding: 16px 20px;
`;

const Grid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
	gap: 12px;
	margin-bottom: 12px;
`;

const FieldLabel = styled.div`
	font-size: 11px;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.03em;
	color: #64748b;
	margin-bottom: 2px;
`;

const FieldValue = styled.div`
	font-size: 13px;
	color: #0f172a;
	word-break: break-word;
`;

const FullWidthField = styled.div`
	margin-bottom: 12px;
`;

const MetadataBlock = styled.pre`
	margin: 0;
	background: #0f172a;
	color: #e2e8f0;
	padding: 12px;
	border-radius: 8px;
	font-size: 12px;
	white-space: pre-wrap;
	word-break: break-word;
	max-width: 100%;
	overflow-x: auto;
`;

export default function LogRowDetail(props: { log: AdminStationPortalLogRecord }) {
	const { log } = props;
	return (
		<Wrap>
			<Grid>
				<div>
					<FieldLabel>Station</FieldLabel>
					<FieldValue>{getStationLabel(log)}</FieldValue>
				</div>
				<div>
					<FieldLabel>Actor</FieldLabel>
					<FieldValue>{getActorLabel(log)}</FieldValue>
				</div>
				<div>
					<FieldLabel>Source</FieldLabel>
					<FieldValue>{log.sourceApp || "-"}</FieldValue>
				</div>
				<div>
					<FieldLabel>IP</FieldLabel>
					<FieldValue>{log.ipAddress || "-"}</FieldValue>
				</div>
				<div>
					<FieldLabel>User agent</FieldLabel>
					<FieldValue>{log.userAgent || "-"}</FieldValue>
				</div>
			</Grid>
			{/*
			 * Ngoài lưới vì message là câu dài nhất — trong bảng nó bị ellipsis cắt
			 * cụt, nên đây là chỗ duy nhất đọc được đủ nội dung lỗi.
			 */}
			<FullWidthField>
				<FieldLabel>Message</FieldLabel>
				<FieldValue data-testid="log-message">{log.message || "-"}</FieldValue>
			</FullWidthField>
			<MetadataBlock data-testid="log-metadata">{formatMetadata(log.metadata)}</MetadataBlock>
		</Wrap>
	);
}
